import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import {
  TrendingUp,
  TrendingDown,
  Target,
  BarChart3,
  MessageSquare,
  RefreshCw,
  ArrowRight,
  Activity,
  Loader2,
  Zap,
  AlertCircle,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const KPIComparisonPanel = ({ companyId, onNavigateToThread }) => {
  const token = localStorage.getItem('token');
  const queryClient = useQueryClient();
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [showChartModal, setShowChartModal] = useState(false);
  const [activeModule, setActiveModule] = useState(null); // Will be set to first module with data

  // Fetch KPI comparisons from ALL modules
  const { data: kpiData, isLoading, error, refetch } = useQuery({
    queryKey: ['kpi-comparisons-all-modules', companyId],
    queryFn: async () => {
      const response = await axios.get(`${API}/api/companies/${companyId}/kpi-comparisons/all-modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!companyId && !!token,
    refetchInterval: 30000,
    retry: 1,
    staleTime: 10000
  });

  // Set initial active module when data loads
  React.useEffect(() => {
    if (kpiData?.modules && !activeModule) {
      const modulesWithData = Object.keys(kpiData.modules);
      if (modulesWithData.length > 0) {
        setActiveModule(modulesWithData[0]);
      }
    }
  }, [kpiData, activeModule]);

  // Don't render if no companyId
  if (!companyId) {
    return null;
  }

  // Module display names
  const moduleNames = {
    revenue: 'Revenue',
    operations: 'Operaciones',
    finance: 'Finanzas',
    marketing: 'Marketing',
    hr: 'RRHH',
    strategy: 'Estrategia'
  };

  // Create thread from KPI mutation
  const createThreadMutation = useMutation({
    mutationFn: async (kpi) => {
      const response = await axios.post(`${API}/api/companies/${companyId}/threads/from-kpi`, kpi, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onMutate: () => {
      toast.loading('🚀 Generando Ruta Estratégica...', {
        id: 'strategic-route-generation',
        description: 'El Super Agente de Gobernanza está analizando los datos. Esto puede tardar 30-60 segundos.',
        duration: Infinity
      });
    },
    onSuccess: (data) => {
      toast.dismiss('strategic-route-generation');
      toast.success('✅ Ruta Estratégica generada', {
        description: 'La ruta aparecerá en el módulo de Rutas Estratégicas y Mission Control.'
      });
      queryClient.invalidateQueries(['threads', companyId]);
      queryClient.invalidateQueries(['insights-reports', companyId]);
      setShowChartModal(false);
      if (onNavigateToThread) {
        onNavigateToThread(data);
      }
    },
    onError: (error) => {
      toast.dismiss('strategic-route-generation');
      toast.error('❌ Error al crear la ruta estratégica', {
        description: error.response?.data?.detail || 'Por favor intenta de nuevo.'
      });
    }
  });

  const deleteKpiMutation = useMutation({
    mutationFn: async (kpiId) => {
      const response = await axios.delete(`${API}/api/companies/${companyId}/kpi-comparisons/${kpiId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('KPI eliminado');
      queryClient.invalidateQueries(['kpi-comparisons', companyId]);
    },
    onError: (error) => {
      toast.error('Error al eliminar KPI', {
        description: error.response?.data?.detail || 'Intenta de nuevo'
      });
    }
  });

  const handleDeleteKpi = (kpi, e) => {
    e.stopPropagation();
    if (window.confirm(`¿Eliminar KPI "${kpi.kpi_name}"?`)) {
      deleteKpiMutation.mutate(kpi.id);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'above':
        return <TrendingUp className="w-4 h-4 text-emerald-400" />;
      case 'below':
        return <TrendingDown className="w-4 h-4 text-red-400" />;
      default:
        return <Target className="w-4 h-4 text-blue-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'above':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Por encima</Badge>;
      case 'below':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Por debajo</Badge>;
      default:
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">En objetivo</Badge>;
    }
  };

  const formatValue = (value, type) => {
    if (value === null || value === undefined) return '-';
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    
    if (type === 'percentage' || type === 'percent') {
      return `${numValue.toFixed(1)}%`;
    }
    if (type === 'currency' || type === 'money') {
      return `Q${numValue.toLocaleString('es-GT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    if (numValue >= 1000000) {
      return `${(numValue / 1000000).toFixed(2)}M`;
    }
    if (numValue >= 1000) {
      return `${(numValue / 1000).toFixed(1)}K`;
    }
    return numValue.toLocaleString('es-GT', { maximumFractionDigits: 2 });
  };

  const handleKPIClick = (kpi) => {
    setSelectedKPI(kpi);
    setShowChartModal(true);
  };

  const handleAnalyzeKPI = () => {
    if (!selectedKPI) return;
    createThreadMutation.mutate(selectedKPI);
  };

  const handleQuickAnalyze = (kpi, e) => {
    e.stopPropagation();
    setSelectedKPI(kpi);
    createThreadMutation.mutate(kpi);
  };

  // Normalize module_type values (fix inconsistencies like "ModuleType.REVENUE" vs "revenue")
  const normalizeModuleType = (moduleType) => {
    if (!moduleType) return 'general';
    
    // Remove "ModuleType." prefix if present
    let normalized = moduleType.replace(/^ModuleType\./i, '');
    
    // Convert to lowercase for consistency
    normalized = normalized.toLowerCase();
    
    // Map to display names
    const moduleDisplayNames = {
      'revenue': 'Revenue',
      'operations': 'Operaciones',
      'commercial': 'Comercial',
      'rrhh': 'RRHH',
      'finanzas': 'Finanzas',
      'finance': 'Finanzas',
      'general': 'General'
    };
    
    return moduleDisplayNames[normalized] || normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  // Get normalized module type for comparison
  const getNormalizedModuleKey = (moduleType) => {
    if (!moduleType) return 'general';
    return moduleType.replace(/^ModuleType\./i, '').toLowerCase();
  };

  // Get unique modules from the new data structure
  const modulesWithData = useMemo(() => {
    if (!kpiData?.modules) return [];
    return Object.keys(kpiData.modules);
  }, [kpiData]);

  // Get KPIs for the active module
  const activeModuleData = useMemo(() => {
    if (!kpiData?.modules || !activeModule) return null;
    return kpiData.modules[activeModule];
  }, [kpiData, activeModule]);

  // Group KPIs by agent within active module
  const groupedComparisons = useMemo(() => {
    if (!activeModuleData?.kpis) return {};
    
    const grouped = {};
    activeModuleData.kpis.forEach(kpi => {
      const agent = kpi.agent_name || 'Agente Funcional 001';
      if (!grouped[agent]) {
        grouped[agent] = [];
      }
      grouped[agent].push(kpi);
    });
    
    return grouped;
  }, [activeModuleData]);

  // Chart data for modal
  const chartData = selectedKPI ? [
    { name: 'Comparación', 'Valor Actual': selectedKPI.actual_value, 'Benchmark': selectedKPI.benchmark_value }
  ] : [];

  const actualColor = selectedKPI?.status === 'above' ? '#10b981' : selectedKPI?.status === 'below' ? '#ef4444' : '#3b82f6';
  const benchmarkColor = '#f59e0b';

  // Mini chart component - alternates between bar and sparkline
  const MiniChart = ({ kpi, index }) => {
    const progress = kpi.benchmark_value > 0 
      ? Math.min((kpi.actual_value / kpi.benchmark_value) * 100, 150) 
      : 0;
    
    const isSparkline = index % 3 === 1; // Every 3rd card uses sparkline
    const isAreaChart = index % 3 === 2; // Every 3rd uses area
    
    const statusColor = kpi.status === 'above' ? '#10b981' : kpi.status === 'below' ? '#ef4444' : '#3b82f6';
    
    // Generate fake sparkline data for visual variety
    const sparklineData = useMemo(() => {
      const base = kpi.actual_value;
      const variation = base * 0.15;
      return [
        { v: base - variation * 0.5 },
        { v: base - variation * 0.3 },
        { v: base + variation * 0.2 },
        { v: base - variation * 0.1 },
        { v: base + variation * 0.4 },
        { v: base }
      ];
    }, [kpi.actual_value]);
    
    if (isSparkline) {
      return (
        <div className="h-12 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparklineData}>
              <Line 
                type="monotone" 
                dataKey="v" 
                stroke={statusColor} 
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    }
    
    if (isAreaChart) {
      return (
        <div className="h-12 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparklineData}>
              <defs>
                <linearGradient id={`gradient-${kpi.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={statusColor} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={statusColor} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <Area 
                type="monotone" 
                dataKey="v" 
                stroke={statusColor} 
                strokeWidth={2}
                fill={`url(#gradient-${kpi.id})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      );
    }
    
    // Default: Progress bar
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-slate-500">0</span>
          <span className="text-slate-500">Benchmark</span>
        </div>
        <div className="h-3 bg-slate-700/50 rounded-full overflow-hidden relative">
          {/* Benchmark marker */}
          <div 
            className="absolute top-0 bottom-0 w-0.5 bg-amber-500 z-10"
            style={{ left: '66.67%' }}
          />
          {/* Progress */}
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ 
              width: `${Math.min(progress * 0.6667, 100)}%`,
              backgroundColor: statusColor
            }}
          />
        </div>
      </div>
    );
  };

  // KPI Card Component
  const KPICard = ({ kpi, index }) => {
    const hasBenchmark = kpi.has_benchmark !== false && kpi.benchmark_value !== null;
    const hasValue = kpi.actual_value !== null && kpi.actual_value !== undefined;
    const isAbove = kpi.status === 'above';
    const isBelow = kpi.status === 'below';
    const isNoBenchmark = !hasBenchmark || kpi.status === 'no_benchmark';
    const isNoValue = kpi.status === 'no_value' || kpi.status === 'no_data' || !hasValue;
    
    const borderColor = isNoValue ? 'border-amber-500/30' : isNoBenchmark ? 'border-slate-500/30' : isAbove ? 'border-emerald-500/30' : isBelow ? 'border-red-500/30' : 'border-blue-500/30';
    const glowColor = isNoValue ? 'hover:shadow-amber-500/10' : isNoBenchmark ? 'hover:shadow-slate-500/10' : isAbove ? 'hover:shadow-emerald-500/10' : isBelow ? 'hover:shadow-red-500/10' : 'hover:shadow-blue-500/10';
    const valueColor = isNoValue ? 'text-amber-400' : isNoBenchmark ? 'text-slate-300' : isAbove ? 'text-emerald-400' : isBelow ? 'text-red-400' : 'text-blue-400';
    
    return (
      <Card 
        className={`glass-dark ${borderColor} hover:shadow-lg ${glowColor} transition-all duration-300 cursor-pointer group`}
        onClick={() => handleKPIClick(kpi)}
      >
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-white truncate group-hover:text-blue-300 transition-colors">
                {kpi.kpi_name}
              </h4>
              <p className="text-xs text-slate-500 truncate mt-0.5">
                {kpi.module_type?.replace(/_/g, ' ')}
                {kpi.unit && kpi.unit !== 'number' && ` • ${kpi.unit}`}
              </p>
            </div>
            {isNoValue ? (
              <div className="p-1.5 rounded-full bg-amber-500/20">
                <AlertCircle className="w-4 h-4 text-amber-400" />
              </div>
            ) : hasBenchmark ? getStatusIcon(kpi.status) : (
              <div className="p-1.5 rounded-full bg-slate-500/20">
                <BarChart3 className="w-4 h-4 text-slate-400" />
              </div>
            )}
          </div>
          
          {/* Value */}
          <div className="mb-3">
            {hasValue ? (
              <>
                <p className={`text-2xl font-bold ${valueColor}`}>
                  {formatValue(kpi.actual_value, kpi.metric_type)}
                </p>
                {hasBenchmark && !isNoValue ? (
                  <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                    <span className={`font-medium ${isAbove ? 'text-emerald-400' : isBelow ? 'text-red-400' : 'text-blue-400'}`}>
                      {kpi.difference_percentage > 0 ? '+' : ''}{kpi.difference_percentage}%
                    </span>
                    <span>vs benchmark ({formatValue(kpi.benchmark_value, kpi.metric_type)})</span>
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">
                    {hasBenchmark ? `Benchmark: ${formatValue(kpi.benchmark_value, kpi.metric_type)}` : 'Sin benchmark'}
                  </p>
                )}
              </>
            ) : (
              <>
                <p className={`text-xl font-bold ${valueColor}`}>
                  Sin valor
                </p>
                <p className="text-xs text-amber-400/70 mt-1">
                  {hasBenchmark ? `Benchmark: ${formatValue(kpi.benchmark_value, kpi.metric_type)}` : 'Pendiente de datos'}
                </p>
              </>
            )}
          </div>
          
          {/* Mini Chart - only show if has benchmark AND has value */}
          {hasBenchmark && hasValue && (
            <div className="mb-3">
              <MiniChart kpi={kpi} index={index} />
            </div>
          )}
          
          {/* Simple value bar for KPIs without benchmark or value */}
          {(!hasBenchmark || !hasValue) && (
            <div className="mb-3 h-8 flex items-center">
              <div className="w-full bg-slate-700/50 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${hasValue ? 'bg-gradient-to-r from-slate-500 to-slate-400' : 'bg-gradient-to-r from-amber-600/50 to-amber-500/50'}`}
                  style={{ width: hasValue ? '100%' : '30%' }}
                />
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-1">
            <Button 
              size="sm" 
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-xs"
              onClick={(e) => handleQuickAnalyze(kpi, e)}
              disabled={createThreadMutation.isPending}
            >
              {createThreadMutation.isPending && selectedKPI?.id === kpi.id ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Analizando...
                </>
              ) : (
                <>
                  <Zap className="w-3 h-3 mr-1" />
                  Analizar con Ruta Estratégica
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
              onClick={(e) => handleDeleteKpi(kpi, e)}
              disabled={deleteKpiMutation.isPending}
              data-testid={`delete-kpi-${kpi.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Card className="glass-dark border-slate-700">
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            <span className="ml-3 text-slate-400">Cargando KPIs...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    if (error.response?.status === 404) {
      return (
        <Card className="glass-dark border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              Comparación con Benchmarks
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8 text-center">
            <Activity className="w-12 h-12 mx-auto text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-slate-300 mb-2">Sin comparaciones de KPIs</h3>
            <p className="text-slate-500 text-sm">
              Ejecuta una ingesta de datos para generar automáticamente comparaciones con benchmarks de tu industria.
            </p>
          </CardContent>
        </Card>
      );
    }
    
    return (
      <Card className="glass-dark border-red-500/30">
        <CardContent className="p-8 text-center">
          <p className="text-red-400 mb-4">Error al cargar los KPIs</p>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reintentar
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!kpiData?.modules || Object.keys(kpiData.modules).length === 0) {
    return (
      <Card className="glass-dark border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-400" />
            Comparación con Benchmarks
          </CardTitle>
          <CardDescription className="text-slate-400">
            Los KPIs se generarán automáticamente al ejecutar la ingesta de datos
          </CardDescription>
        </CardHeader>
        <CardContent className="p-8 text-center">
          <Activity className="w-12 h-12 mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">Sin comparaciones de KPIs</h3>
          <p className="text-slate-500 text-sm">
            Ejecuta una ingesta de datos para generar automáticamente comparaciones con benchmarks de tu industria.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="glass-dark border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                Mission Control - KPIs vs Benchmarks
              </CardTitle>
              <CardDescription className="text-slate-400">
                {kpiData.global_summary?.total || 0} métricas en {kpiData.global_summary?.modules_with_data || 0} módulos • 
                Rendimiento: {kpiData.global_summary?.performance_score || 0}%
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              {/* Summary badges */}
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-500/20 text-emerald-400">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  {kpiData.global_summary?.above_benchmark || 0} arriba
                </Badge>
                <Badge className="bg-red-500/20 text-red-400">
                  <TrendingDown className="w-3 h-3 mr-1" />
                  {kpiData.global_summary?.below_benchmark || 0} abajo
                </Badge>
                <Badge className="bg-blue-500/20 text-blue-400">
                  <Target className="w-3 h-3 mr-1" />
                  {kpiData.global_summary?.on_target || 0} en meta
                </Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={() => refetch()}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Module Tabs */}
          <Tabs value={activeModule || ''} onValueChange={setActiveModule} className="w-full mb-6">
            <TabsList className="bg-slate-800/50 flex-wrap h-auto gap-1 p-1">
              {modulesWithData.map((moduleKey) => {
                const moduleInfo = kpiData.modules[moduleKey];
                const kpiCount = moduleInfo?.kpis?.length || 0;
                return (
                  <TabsTrigger 
                    key={moduleKey} 
                    value={moduleKey} 
                    className="data-[state=active]:bg-blue-600 px-3 py-2"
                  >
                    <span className="flex items-center gap-2">
                      {moduleNames[moduleKey] || moduleKey}
                      <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5">
                        {kpiCount}
                      </Badge>
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          {/* Active Module Info */}
          {activeModuleData && (
            <div className="mb-4 p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">
                  Última ingesta: {activeModuleData.job?.file_name || 'N/A'}
                </span>
                <span className="text-slate-500">
                  {activeModuleData.job?.created_at && new Date(activeModuleData.job.created_at).toLocaleDateString('es-GT', { 
                    day: '2-digit', 
                    month: 'short', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="text-emerald-400">▲ {activeModuleData.summary?.above || 0} arriba</span>
                <span className="text-red-400">▼ {activeModuleData.summary?.below || 0} abajo</span>
                <span className="text-blue-400">● {activeModuleData.summary?.on_target || 0} en meta</span>
                <span className="text-slate-400">○ {activeModuleData.summary?.no_benchmark || 0} sin benchmark</span>
              </div>
            </div>
          )}

          {/* KPI Cards Grid */}
          <div className="space-y-6">
            {Object.entries(groupedComparisons).map(([agentName, kpis]) => (
              <div key={agentName}>
                {/* Agent Header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-5 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full" />
                  <h4 className="text-sm font-medium text-slate-300">{agentName}</h4>
                  <Badge variant="outline" className="text-xs">{kpis.length} KPIs</Badge>
                </div>
                
                {/* KPI Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {kpis.map((kpi, index) => (
                    <KPICard key={kpi.id || `${kpi.kpi_name}-${index}`} kpi={kpi} index={index} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* KPI Chart Modal */}
      <Dialog open={showChartModal} onOpenChange={setShowChartModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              {selectedKPI?.kpi_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedKPI && (
            <div className="space-y-6">
              {/* Chart - Barras comparativas */}
              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} barGap={8}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                    <XAxis dataKey="name" stroke="#94a3b8" tick={false} />
                    <YAxis stroke="#94a3b8" tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: '1px solid #334155',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#f1f5f9' }}
                      formatter={(value) => formatValue(value, selectedKPI.metric_type)}
                    />
                    <Legend />
                    <Bar dataKey="Valor Actual" fill={actualColor} radius={[4, 4, 0, 0]} name="Tu Valor" />
                    <Bar dataKey="Benchmark" fill={benchmarkColor} radius={[4, 4, 0, 0]} name="Benchmark Industria" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* KPI Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg border-2 ${selectedKPI.status === 'above' ? 'bg-emerald-500/10 border-emerald-500/30' : selectedKPI.status === 'below' ? 'bg-red-500/10 border-red-500/30' : 'bg-blue-500/10 border-blue-500/30'}`}>
                  <p className="text-xs text-slate-400 mb-1">Tu Valor Actual</p>
                  <p className={`text-2xl font-bold ${selectedKPI.status === 'above' ? 'text-emerald-400' : selectedKPI.status === 'below' ? 'text-red-400' : 'text-blue-400'}`}>
                    {formatValue(selectedKPI.actual_value, selectedKPI.metric_type)}
                  </p>
                </div>
                <div className="p-4 bg-amber-500/10 rounded-lg border-2 border-amber-500/30">
                  <p className="text-xs text-slate-400 mb-1">Benchmark de Industria</p>
                  <p className="text-2xl font-bold text-amber-400">
                    {formatValue(selectedKPI.benchmark_value, selectedKPI.metric_type)}
                  </p>
                </div>
              </div>
              
              {/* Difference indicator */}
              <div className={`p-3 rounded-lg text-center ${selectedKPI.status === 'above' ? 'bg-emerald-500/20' : selectedKPI.status === 'below' ? 'bg-red-500/20' : 'bg-blue-500/20'}`}>
                <p className={`text-lg font-semibold ${selectedKPI.status === 'above' ? 'text-emerald-400' : selectedKPI.status === 'below' ? 'text-red-400' : 'text-blue-400'}`}>
                  {selectedKPI.status === 'above' ? '📈 Por encima del benchmark' : selectedKPI.status === 'below' ? '📉 Por debajo del benchmark' : '🎯 En el objetivo'}
                  <span className="ml-2">
                    ({selectedKPI.difference_percentage > 0 ? '+' : ''}{selectedKPI.difference_percentage}%)
                  </span>
                </p>
              </div>

              {/* Context */}
              <div className="p-4 bg-slate-800/30 rounded-lg">
                <p className="text-xs text-slate-400 mb-2">Contexto de Industria</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-slate-300">{selectedKPI.industry}</Badge>
                  {selectedKPI.sub_industry && (
                    <Badge variant="outline" className="text-slate-300">{selectedKPI.sub_industry}</Badge>
                  )}
                  {selectedKPI.business_model && (
                    <Badge variant="outline" className="text-slate-300">{selectedKPI.business_model}</Badge>
                  )}
                </div>
              </div>

              {/* Action Button with loading state */}
              <Button 
                className="w-full btn-gradient"
                onClick={handleAnalyzeKPI}
                disabled={createThreadMutation.isPending}
              >
                {createThreadMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creando Ruta Estratégica... Por favor espera
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Analizar en Ruta Estratégica
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              
              {/* Loading message when processing */}
              {createThreadMutation.isPending && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-400 mb-2" />
                  <p className="text-blue-300 text-sm font-medium">
                    🤖 El Super Agente de Gobernanza está analizando tu KPI...
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    Este proceso puede tomar entre 20-40 segundos
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default KPIComparisonPanel;
